import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { BarChart3, Loader2, RefreshCw, Table2, TrendingUp } from 'lucide-react';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  salesDashboardApiService,
  ProductActivity,
  SalesByCategory,
  SalesByCustomer,
  TopProductBySales,
} from '../services/salesDashboardApi';

const PIE_COLORS = ['#3b82f6', '#f97316', '#10b981', '#a855f7', '#ef4444', '#14b8a6', '#f59e0b', '#6366f1'];
const RADIAN = Math.PI / 180;

/** Position value labels outside the slice without clipping at card edges. */
function renderCategoryValueLabel(props: {
  cx?: number;
  cy?: number;
  midAngle?: number;
  outerRadius?: number;
  value?: number;
}) {
  const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0, value } = props;
  if (value == null || Number(value) <= 0) return null;

  const radius = outerRadius + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
    >
      {formatNumberIN(value)}
    </text>
  );
}

function formatCurrency(value: number | null | undefined): string {
  const v = Number(value ?? 0) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(v);
}

function formatNumberIN(value: number | null | undefined): string {
  const v = Number(value ?? 0) || 0;
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);
}

function SalesChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ payload?: { name?: string; value?: number; quantity?: number }; value?: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const name = row?.name ?? label ?? '—';
  const amount = Number(row?.value ?? payload[0]?.value ?? 0) || 0;
  const quantity = Number(row?.quantity ?? 0) || 0;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm text-sm">
      <p className="font-medium text-gray-900 mb-1">{name}</p>
      <p className="text-blue-600">Amount: {formatCurrency(amount)}</p>
      <p className="text-gray-700">Quantity: {formatNumberIN(quantity)}</p>
    </div>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toISODate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayDate(): string {
  return toISODate(new Date());
}

type DatePreset = '30d' | '60d' | '90d' | '6m' | '1y' | 'custom';

const DATE_PRESETS: { id: Exclude<DatePreset, 'custom'>; label: string }[] = [
  { id: '30d', label: '30 Days' },
  { id: '60d', label: '60 Days' },
  { id: '90d', label: '90 Days' },
  { id: '6m', label: '6 Months' },
  { id: '1y', label: '1 Year' },
];

function getRangeForPreset(preset: Exclude<DatePreset, 'custom'>): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case '30d':
      start.setDate(end.getDate() - 29);
      break;
    case '60d':
      start.setDate(end.getDate() - 59);
      break;
    case '90d':
      start.setDate(end.getDate() - 89);
      break;
    case '6m':
      start.setMonth(end.getMonth() - 6);
      break;
    case '1y':
      start.setFullYear(end.getFullYear() - 1);
      break;
  }
  return { startDate: toISODate(start), endDate: toISODate(end) };
}

function getLast30DaysRange(): { startDate: string; endDate: string } {
  return getRangeForPreset('30d');
}

export function SalesDashboard() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [byCategory, setByCategory] = useState<SalesByCategory[]>([]);
  const [byCustomer, setByCustomer] = useState<SalesByCustomer[]>([]);
  const [topProducts, setTopProducts] = useState<TopProductBySales[]>([]);
  const [productActivity, setProductActivity] = useState<ProductActivity[]>([]);
  const [customerView, setCustomerView] = useState<'chart' | 'table'>('chart');
  const [categoryView, setCategoryView] = useState<'chart' | 'table'>('chart');
  const [productsView, setProductsView] = useState<'chart' | 'table'>('chart');

  const defaultRange = useMemo(() => getLast30DaysRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [appliedStartDate, setAppliedStartDate] = useState(defaultRange.startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(defaultRange.endDate);
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');

  const branchId = selectedBranch ?? user?.defaultBranchId;

  const getActivityDays = (from: string, to: string) => {
    const start = new Date(from);
    const end = new Date(to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 30;
    const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, diff);
  };

  const load = async (from = startDate, to = endDate) => {
    if (!from || !to) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (new Date(from) > new Date(to)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    try {
      setLoading(true);
      const activityDays = getActivityDays(from, to);
      const [c, cust, top, activity] = await Promise.all([
        salesDashboardApiService.getSalesByCategory({ branchId: branchId ?? undefined, startDate: from, endDate: to }),
        salesDashboardApiService.getSalesByCustomer({ branchId: branchId ?? undefined, startDate: from, endDate: to }),
        salesDashboardApiService.getTopProducts({ branchId: branchId ?? undefined, startDate: from, endDate: to, limit: 10 }),
        salesDashboardApiService.getProductActivity({ branchId: branchId ?? undefined, days: activityDays }),
      ]);

      setByCategory(Array.isArray(c) ? c : []);
      setByCustomer(Array.isArray(cust) ? cust : []);
      setTopProducts(Array.isArray(top) ? top : []);
      setProductActivity(Array.isArray(activity) ? activity : []);
      setAppliedStartDate(from);
      setAppliedEndDate(to);
    } catch (e) {
      toast.error('Failed to load sales dashboard');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (preset: Exclude<DatePreset, 'custom'>) => {
    const range = getRangeForPreset(preset);
    setDatePreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    load(range.startDate, range.endDate);
  };

  useEffect(() => {
    load(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const categoryRows = useMemo(() => {
    return (byCategory || [])
      .map((r: any) => ({
        name: String(r.categoryName ?? r.category ?? 'N/A'),
        net: Number(r.netSalesAmount ?? 0) || 0,
        tax: Number(r.taxAmount ?? 0) || 0,
        value:
          Number(
            r.grossSalesAmount ??
              r.netSalesAmount ??
              r.totalSalesValue ??
              r.totalSales ??
              r.totalAmount ??
              0
          ) || 0,
        quantity: Number(r.totalBilledQuantity ?? r.billedQuantity ?? r.quantity ?? 0) || 0,
      }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [byCategory]);

  const pieData = useMemo(
    () => categoryRows.map((r) => ({ name: r.name, value: r.value, quantity: r.quantity })),
    [categoryRows]
  );

  const kpis = useMemo(() => {
    const rows = byCategory || [];
    const net = rows.reduce((s: number, r: any) => s + (Number(r.netSalesAmount ?? 0) || 0), 0);
    const tax = rows.reduce((s: number, r: any) => s + (Number(r.taxAmount ?? 0) || 0), 0);
    const gross = rows.reduce(
      (s: number, r: any) =>
        s +
        (Number(
          r.grossSalesAmount ??
            ((Number(r.netSalesAmount ?? 0) || 0) + (Number(r.taxAmount ?? 0) || 0))
        ) || 0),
      0
    );
    return { net, tax, gross };
  }, [byCategory]);

  const customerRows = useMemo(() => {
    return (byCustomer || [])
      .map((r: any) => ({
        name: String(r.customerName ?? r.customer ?? 'N/A'),
        net: Number(r.netSalesAmount ?? 0) || 0,
        tax: Number(r.taxAmount ?? 0) || 0,
        value:
          Number(
            r.grossSalesAmount ??
              r.netSalesAmount ??
              r.totalSalesValue ??
              r.totalSales ??
              r.totalAmount ??
              0
          ) || 0,
        quantity: Number(r.totalBilledQuantity ?? r.billedQuantity ?? r.quantity ?? 0) || 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [byCustomer]);

  const customerBarData = useMemo(() => customerRows.slice(0, 15), [customerRows]);

  const productRows = useMemo(() => {
    return (topProducts || [])
      .map((r: any) => ({
        name: String(r.productName ?? r.itemName ?? 'N/A'),
        categoryName: String(r.categoryName ?? ''),
        net: Number(r.netSalesAmount ?? 0) || 0,
        tax: Number(r.taxAmount ?? 0) || 0,
        value:
          Number(
            r.grossSalesAmount ??
              r.netSalesAmount ??
              r.totalSalesValue ??
              r.totalSales ??
              r.totalAmount ??
              0
          ) || 0,
        quantity: Number(r.totalBilledQuantity ?? r.quantity ?? 0) || 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [topProducts]);

  const topProductsBarData = useMemo(() => productRows.slice(0, 10), [productRows]);

  // const inactiveProducts = useMemo(() => {
  //   return (productActivity || []).filter((p: any) => {
  //     const isActive = p.isActive;
  //     if (typeof isActive === 'boolean') return !isActive;
  //     const cnt = Number(p.invoiceCountLastNDays ?? p.invoiceCount ?? 0) || 0;
  //     return cnt <= 0;
  //   });
  // }, [productActivity]);

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
            <div>
              <CardTitle className="text-xl text-gray-900">Sales Dashboard</CardTitle>
              <p className="text-gray-600 text-sm">
                {formatDate(appliedStartDate)} to {formatDate(appliedEndDate)}
              </p>
            </div>
            <div className="flex justify-center">
              <div className="inline-flex flex-wrap rounded-lg border border-primary-200 bg-white p-1">
                {DATE_PRESETS.map((preset) => (
                  <Button
                    key={preset.id}
                    type="button"
                    size="sm"
                    variant={datePreset === preset.id ? 'default' : 'ghost'}
                    disabled={loading}
                    onClick={() => applyPreset(preset.id)}
                    className={
                      datePreset === preset.id
                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                        : 'text-gray-700 hover:bg-primary-50'
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-3 lg:justify-end">
              <div className="space-y-1">
                <Label htmlFor="sales-startDate" className="text-xs text-gray-600">From</Label>
                <Input
                  id="sales-startDate"
                  type="date"
                  value={startDate}
                  max={endDate || getTodayDate()}
                  onChange={(e) => {
                    setDatePreset('custom');
                    setStartDate(e.target.value);
                  }}
                  className="w-40 border-primary-200 bg-white"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sales-endDate" className="text-xs text-gray-600">To</Label>
                <Input
                  id="sales-endDate"
                  type="date"
                  value={endDate}
                  max={getTodayDate()}
                  min={startDate || undefined}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    const today = getTodayDate();
                    setDatePreset('custom');
                    if (selectedDate <= today) {
                      setEndDate(selectedDate);
                    } else {
                      setEndDate(today);
                      toast.error('End date cannot be more than today');
                    }
                  }}
                  className="w-40 border-primary-200 bg-white"
                />
              </div>
              <Button
                onClick={() => load()}
                size="sm"
                disabled={loading}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Apply</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border border-primary-200 bg-white">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-lg font-semibold text-primary-700">{formatCurrency(kpis.net)}</p>
              </CardContent>
            </Card>
            <Card className="border border-primary-200 bg-white">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Total Tax Amount</p>
                <p className="text-lg font-semibold text-primary-700">{formatCurrency(kpis.tax)}</p>
              </CardContent>
            </Card>
            <Card className="border border-primary-200 bg-white">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Grand Total</p>
                <p className="text-lg font-semibold text-primary-700">{formatCurrency(kpis.gross)}</p>
              </CardContent>
            </Card>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
              <p className="text-gray-600">Loading sales dashboard...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border border-primary-200">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-lg">Sales by Customer</CardTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCustomerView((prev) => (prev === 'chart' ? 'table' : 'chart'))}
                      className="border-primary-200 text-primary-700 hover:bg-primary-50"
                    >
                      {customerView === 'chart' ? (
                        <>
                          <Table2 className="h-4 w-4 mr-2" />
                          Show Table
                        </>
                      ) : (
                        <>
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Show Chart
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className={customerView === 'table' ? 'p-0' : 'p-4'}>
                  {customerRows.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">No data</div>
                  ) : customerView === 'chart' ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={customerBarData} layout="vertical" margin={{ top: 10, right: 20, left: 80, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={(v) => `${Math.round(Number(v) / 100000)}L`} />
                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
                        <Tooltip content={<SalesChartTooltip />} />
                        <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="overflow-x-auto max-h-[360px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold">Customer</TableHead>
                            <TableHead className="font-semibold text-right">Qty</TableHead>
                            <TableHead className="font-semibold text-right">Amount</TableHead>
                            <TableHead className="font-semibold text-right">Tax</TableHead>
                            <TableHead className="font-semibold text-right">Grand Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {customerRows.map((row, idx) => (
                            <TableRow key={`${row.name}-${idx}`} className="hover:bg-primary-50/50">
                              <TableCell className="font-medium">{row.name}</TableCell>
                              <TableCell className="text-right">{formatNumberIN(row.quantity)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.net)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.tax)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card className="border border-primary-200">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary-600" />
                      Sales by Product Category
                    </CardTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCategoryView((prev) => (prev === 'chart' ? 'table' : 'chart'))}
                      className="border-primary-200 text-primary-700 hover:bg-primary-50"
                    >
                      {categoryView === 'chart' ? (
                        <>
                          <Table2 className="h-4 w-4 mr-2" />
                          Show Table
                        </>
                      ) : (
                        <>
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Show Chart
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className={categoryView === 'table' ? 'p-0' : 'p-4 overflow-visible'}>
                  {categoryRows.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">No data</div>
                  ) : categoryView === 'chart' ? (
                    <div className="overflow-visible w-full min-h-[400px]">
                      <ResponsiveContainer width="100%" height={400}>
                        <PieChart margin={{ top: 48, right: 56, bottom: 48, left: 56 }}>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="46%"
                            outerRadius={82}
                            labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                            label={renderCategoryValueLabel}
                          >
                            {pieData.map((_, idx) => (
                              <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<SalesChartTooltip />} />
                          <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold">Category</TableHead>
                            <TableHead className="font-semibold text-right">Qty</TableHead>
                            <TableHead className="font-semibold text-right">Amount</TableHead>
                            <TableHead className="font-semibold text-right">Tax</TableHead>
                            <TableHead className="font-semibold text-right">Grand Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {categoryRows.map((row, idx) => (
                            <TableRow key={`${row.name}-${idx}`} className="hover:bg-primary-50/50">
                              <TableCell className="font-medium">{row.name}</TableCell>
                              <TableCell className="text-right">{formatNumberIN(row.quantity)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.net)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.tax)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border border-primary-200 lg:col-span-2">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-lg">Top 10 Products by Sales</CardTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setProductsView((prev) => (prev === 'chart' ? 'table' : 'chart'))}
                      className="border-primary-200 text-primary-700 hover:bg-primary-50"
                    >
                      {productsView === 'chart' ? (
                        <>
                          <Table2 className="h-4 w-4 mr-2" />
                          Show Table
                        </>
                      ) : (
                        <>
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Show Chart
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className={productsView === 'table' ? 'p-0' : 'p-4'}>
                  {productRows.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">No data</div>
                  ) : productsView === 'chart' ? (
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart data={topProductsBarData} layout="vertical" margin={{ top: 10, right: 20, left: 100, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={(v) => `${Math.round(Number(v) / 100000)}L`} />
                        <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 12 }} />
                        <Tooltip content={<SalesChartTooltip />} />
                        <Bar dataKey="value" fill="#f97316" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="overflow-x-auto max-h-[360px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold">Product</TableHead>
                            <TableHead className="font-semibold">Category</TableHead>
                            <TableHead className="font-semibold text-right">Qty</TableHead>
                            <TableHead className="font-semibold text-right">Amount</TableHead>
                            <TableHead className="font-semibold text-right">Tax</TableHead>
                            <TableHead className="font-semibold text-right">Grand Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {productRows.map((row, idx) => (
                            <TableRow key={`${row.name}-${idx}`} className="hover:bg-primary-50/50">
                              <TableCell className="font-medium">{row.name}</TableCell>
                              <TableCell>{row.categoryName || '—'}</TableCell>
                              <TableCell className="text-right">{formatNumberIN(row.quantity)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.net)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.tax)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Inactive Products table (disabled for now) */}
              {/* 
              <Card className="border border-primary-200 lg:col-span-2">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <CardTitle className="text-lg">Inactive Products (no invoices in selected date range)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {inactiveProducts.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">No inactive products</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Invoices</TableHead>
                            <TableHead>Last Invoice Date</TableHead>
                            <TableHead className="text-right">Sales</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inactiveProducts.slice(0, 50).map((p: any, idx: number) => (
                            <TableRow key={p.productId ?? idx} className="hover:bg-primary-50/50">
                              <TableCell className="font-medium">{p.productName ?? p.itemName ?? 'N/A'}</TableCell>
                              <TableCell className="text-right">{Number(p.invoiceCountLastNDays ?? 0) || 0}</TableCell>
                              <TableCell>{formatDate(p.lastInvoiceDate)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(Number(p.totalSalesValueLastNDays ?? 0) || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card> 
              */}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
